import { useId, type ReactNode } from 'react';
import { cn } from '@/lib/utils';
import './Field.css';

export interface FieldProps {
  label?: ReactNode;
  /** Helper text shown below the control (e.g. a format hint). */
  hint?: ReactNode;
  /** Error message; when present the control should render its error treatment. */
  error?: ReactNode;
  /** Marks the field visually and semantically as required. */
  required?: boolean;
  /** Hide the label visually but keep it for assistive tech. */
  hideLabel?: boolean;
  className?: string;
  /**
   * Render-prop receiving the wiring ids. Spread `controlProps` onto the input
   * so label association and `aria-describedby` are correct without boilerplate.
   */
  children: (controlProps: FieldControlProps) => ReactNode;
}

export interface FieldControlProps {
  id: string;
  'aria-describedby'?: string;
  'aria-invalid'?: true;
  'aria-required'?: true;
}

/**
 * Accessible label + hint + error scaffold around a single control. Owns the
 * id wiring so every form control associates its label and messages the same
 * way. Validate on blur in callers; errors render below the field.
 */
export function Field({
  label,
  hint,
  error,
  required,
  hideLabel,
  className,
  children,
}: FieldProps): JSX.Element {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  const describedBy =
    [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ') || undefined;

  const controlProps: FieldControlProps = {
    id,
    ...(describedBy ? { 'aria-describedby': describedBy } : {}),
    ...(error ? { 'aria-invalid': true } : {}),
    ...(required ? { 'aria-required': true } : {}),
  };

  return (
    <div className={cn('field', error && 'field--error', className)}>
      {label && (
        <label htmlFor={id} className={cn('field__label', hideLabel && 'sr-only')}>
          {label}
          {required && (
            <span className="field__required" aria-hidden="true">
              {' '}
              *
            </span>
          )}
        </label>
      )}
      {children(controlProps)}
      {hint && !error && (
        <p id={hintId} className="field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="field__error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
